/**
 * sidebar-collapse — wspólny mechanizm zwijania GŁÓWNEGO sidebara (menu aplikacji) do
 * wąskiego paska z ikonami. App-wide: identyczny dla panelu ITE (admin) i panelu klienta
 * (tenant). Stan zwinięcia zapamiętany w localStorage (jeden wspólny klucz — sesja jest
 * i tak albo admin, albo tenant przez osobne subdomeny).
 *
 * Zwinięcie propagowane przez kontekst (nie propsy), bo pozycje menu renderuje wiele
 * osobnych funkcji w Sidebar.tsx (inline mapa admina, InstalledMenuItemNode, HrQuickNav,
 * PinnedBaseNav, CustomLayoutNav, AdminCustomLayoutNav) — każda czyta `useIsSidebarCollapsed()`
 * i chowa etykietę + dokleja tooltip, zamiast kruchego CSS na `<span>` bez klasy-markera.
 *
 * Osobny mechanizm od `menu/ColumnControls.tsx` (tamto zwija KOLUMNY builderów menu).
 */
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const STORAGE_KEY = 'sidebar.collapsed';
const WIDTH_KEY = 'sidebar.width';

/** Granice ręcznego poszerzania rozwiniętego sidebara (px). Domyślna = dotychczasowe w-64. */
export const SIDEBAR_WIDTH = { min: 208, max: 400, default: 248 } as const;

const SidebarCollapseContext = createContext(false);

/** Stan zwinięcia sidebara zapamiętany w localStorage. Wołany w AdminSidebar i TenantSidebar. */
export function useSidebarCollapsedState(): { collapsed: boolean; toggle: () => void } {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
      } catch {
        /* localStorage niedostępny (private mode) — stan tylko w pamięci. */
      }
      return next;
    });
  }, []);
  return { collapsed, toggle };
}

/** Szerokość rozwiniętego sidebara (px) zapamiętana w localStorage, klampowana do granic. */
export function useSidebarWidth(): { widthPx: number; setWidthPx: (w: number) => void } {
  const [widthPx, setWidth] = useState<number>(() => {
    try {
      const n = Number(window.localStorage.getItem(WIDTH_KEY));
      return Number.isFinite(n) && n >= SIDEBAR_WIDTH.min && n <= SIDEBAR_WIDTH.max
        ? n
        : SIDEBAR_WIDTH.default;
    } catch {
      return SIDEBAR_WIDTH.default;
    }
  });
  const setWidthPx = useCallback((w: number) => {
    const clamped = Math.min(SIDEBAR_WIDTH.max, Math.max(SIDEBAR_WIDTH.min, Math.round(w)));
    setWidth(clamped);
    try {
      window.localStorage.setItem(WIDTH_KEY, String(clamped));
    } catch {
      /* localStorage niedostępny — szerokość tylko w pamięci. */
    }
  }, []);
  return { widthPx, setWidthPx };
}

export function SidebarCollapseProvider({
  collapsed,
  children,
}: {
  collapsed: boolean;
  children: ReactNode;
}) {
  return (
    <SidebarCollapseContext.Provider value={collapsed}>{children}</SidebarCollapseContext.Provider>
  );
}

/** Czyta stan zwinięcia w dowolnym rendererze pozycji sidebara. */
export function useIsSidebarCollapsed(): boolean {
  return useContext(SidebarCollapseContext);
}

/**
 * Klasa wiersza pozycji sidebara — spójna dla wszystkich rendererów. Zwinięty → ikona
 * wyśrodkowana, bez odstępu na etykietę; rozwinięty → ikona + etykieta jak dotychczas.
 */
export function sidebarItemClass(collapsed: boolean, isActive: boolean): string {
  return [
    'flex items-center rounded-lg py-2.5 text-sm transition-colors',
    collapsed ? 'justify-center px-2' : 'gap-3 px-4',
    isActive
      ? 'bg-accent-50 text-accent-700 font-medium dark:bg-accent-900/30 dark:text-accent-300'
      : 'text-text-secondary hover:bg-bg-tertiary hover:text-text-primary',
  ].join(' ');
}

/**
 * Klasa wiersza pozycji sidebara TENANTA — wg Figmy „Nav item" (node 10:35, ramka Left panel):
 * wysokość 44, gap 12, padding poziomy 12, radius 12, etykieta Inter Medium 14/22 w kolorze
 * text/secondary (#5c5c6b). Stan aktywny = stonowany brandowy tint (bez ciężkiego wypełnienia).
 * Osobna od `sidebarItemClass` (współdzielonej z panelem ITE) — redesign dotyczy tylko tenanta.
 */
export function tenantSidebarItemClass(collapsed: boolean, isActive: boolean): string {
  // Wysokość 40px (h-10), etykieta Regular domyślnie. Aktywna pozycja = PIGUŁKA (rounded-full):
  // jasnoniebieskie tło + niebieski tekst i ikona, SemiBold (decyzja właścicielki 2026-09-23;
  // kolory skórki w `aside[data-skin] nav` w index.css).
  return [
    'flex items-center rounded-full text-[14px] leading-[22px] transition-colors',
    collapsed ? 'h-10 justify-center px-2' : 'h-10 gap-3 px-3',
    isActive
      ? 'bg-accent-50 text-accent-700 font-semibold dark:bg-accent-900/30 dark:text-accent-300'
      : 'font-normal text-text-secondary hover:bg-bg-tertiary hover:text-text-primary',
  ].join(' ');
}

/**
 * Rozmiar ikony pozycji — 20px w obu stanach (wg Figmy). Argument `collapsed` zostaje w
 * sygnaturze dla zgodności z callerami (i na wypadek gdyby stany znów miały się różnić).
 */
export function sidebarIconClass(_collapsed: boolean): string {
  // Ujednolicone 20px (h-5) w obu stanach — wg Figmy „08.4 Navigation" (ikona 20px) oraz
  // spójnie z zaszytymi sizeClass="h-5 w-5" w Sidebar.tsx. Wcześniej rozwinięty stan dawał
  // 16px (h-4), przez co ikony różniły się rozmiarem zależnie od renderera pozycji.
  return 'h-5 w-5';
}

/**
 * Etykieta pozycji. Rozwinięty → zwykły tekst (`truncate`). Zwinięty → tekst znika, a w
 * zamian pojawia się własny dymek PO PRAWEJ stronie wiersza przy najechaniu/focusie
 * (natywny `title` bywa spóźniony i ląduje przy kursorze — chcemy spójny dymek po prawej).
 *
 * Dymek renderujemy portalem do `document.body` z pozycją `fixed` liczoną z prostokąta
 * wiersza — inaczej `overflow-y-auto` na `<nav>` i wąska szerokość paska (w-14) by go
 * przycięły. Kotwicą jest `display:contents` span (bez własnego boxa), więc jego
 * `parentElement` to sam wiersz (NavLink/a/div).
 */
export function SidebarItemLabel({ children }: { children: ReactNode }) {
  const collapsed = useIsSidebarCollapsed();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [tip, setTip] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!collapsed) return;
    const row = anchorRef.current?.parentElement;
    if (!row) return;
    const show = () => {
      const r = row.getBoundingClientRect();
      setTip({ top: r.top + r.height / 2, left: r.right + 8 });
    };
    const hide = () => setTip(null);
    row.addEventListener('mouseenter', show);
    row.addEventListener('mouseleave', hide);
    row.addEventListener('focusin', show);
    row.addEventListener('focusout', hide);
    return () => {
      row.removeEventListener('mouseenter', show);
      row.removeEventListener('mouseleave', hide);
      row.removeEventListener('focusin', show);
      row.removeEventListener('focusout', hide);
    };
  }, [collapsed]);

  if (!collapsed) return <span className="truncate">{children}</span>;

  return (
    <span ref={anchorRef} className="contents">
      {tip &&
        createPortal(
          <span
            role="tooltip"
            style={{ position: 'fixed', top: tip.top, left: tip.left, zIndex: 60 }}
            className="pointer-events-none -translate-y-1/2 whitespace-nowrap rounded-md border border-border-secondary bg-bg-elevated px-2 py-1 text-xs font-medium text-text-primary shadow-md"
          >
            {children}
          </span>,
          document.body,
        )}
    </span>
  );
}

/**
 * Przełącznik zwinięcia — PASTYLKA na PRAWEJ KRAWĘDZI panelu (redesign „Ant", wybór Eugenii
 * 2026-09-15). Pozycjonowany `absolute` względem panelu (rodzic MUSI być `relative`), pionowo
 * wyśrodkowany, wchodzi do połowy na krawędź (styl Notion/Linear). Chevron pokazuje kierunek
 * akcji. `testId` domyślnie `sidebar-collapse-toggle` (Settings przekazuje własny).
 */
export function SidebarCollapseToggle({
  collapsed,
  onToggle,
  testId = 'sidebar-collapse-toggle',
}: {
  collapsed: boolean;
  onToggle: () => void;
  testId?: string;
}) {
  const { t } = useTranslation();
  const label = collapsed ? t('common.sidebar.expand') : t('common.sidebar.collapse');
  const Icon = collapsed ? ChevronRight : ChevronLeft;
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={label}
      title={label}
      data-testid={testId}
      className="absolute -right-3 top-3 z-30 flex h-6 w-6 items-center justify-center rounded-full border border-border-secondary bg-bg-elevated text-text-muted transition-colors hover:text-accent-600"
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
    </button>
  );
}
